/**
 * Code to wrap an XBlock so that we can embed it in an IFrame
 */
/*eslint-env jquery*/
import xblockCssPath from 'assets/scss/xblock-styles.scss';
import { LxConfig } from 'global/constants';
import { locale } from 'i18n';

interface ExtraInformation {
    username?: string;
    classroomName?: string;
}

function getJsTags(jsUrls: string[], shouldInitializeXBlockRuntime: boolean) {

    // If we are not initializing the runtime, we don't need these js files either.
    if (!shouldInitializeXBlockRuntime) {
        return '';
    }

    const lmsBaseUrl = LxConfig.LmsBaseUrl;

    jsUrls = [
        `${lmsBaseUrl}/static/js/i18n/en/djangojs.js`,
        ...jsUrls,
    ];

    // We can remove this once LMS starts returning lms-xblock_views.js package in the view response.
    if (
        !jsUrls.find((u) => u.includes('runtime.v1')) && // On devstack pipeline doesn't bundle files.
        !jsUrls.find((u) => u.includes('lms-xblock_views')) // On production this bundle should be included.
    ) {
        // XBlock view fragment does not include all dependencies:
        // 1. It does not have the runtime dependencies.
        // 2. Some old XBlocks in edx-platform/common/lib/xmodules depend on shared dependencies loaded in LMS.
        //
        // For now we include them all here.
        // Eventually all the dependencies should be included in the xblock view fragment.
        jsUrls = [
            // Some old libraries are unlikely to change so we include versions from the CDN.
            // `${lmsBaseUrl}/static/common/js/vendor/jquery.js`,
            'https://cdn.jsdelivr.net/npm/jquery@2.2.4/dist/jquery.min.js',
            // `${lmsBaseUrl}/static/js/vendor/jquery-ui.min.js`,
            'https://cdn.jsdelivr.net/npm/jquery-ui-dist@1.12.1/jquery-ui.min.js',
            // `${lmsBaseUrl}/static/js/vendor/jquery.cookie.js`,
            'https://cdn.jsdelivr.net/npm/jquery.cookie@1.4.1/jquery.cookie.min.js',
            // `${lmsBaseUrl}/static/common/js/vendor/jquery.scrollTo.js`,
            'https://cdn.jsdelivr.net/npm/jquery.scrollto@2.1.2/jquery.scrollTo.min.js',
            // `${lmsBaseUrl}/static/common/js/vendor/underscore.js`,
            'https://cdn.jsdelivr.net/npm/underscore@1.8.3/underscore.min.js',
            `${lmsBaseUrl}/static/common/js/xblock/core.js`,
            `${lmsBaseUrl}/static/common/js/xblock/runtime.v1.js`,
            `${lmsBaseUrl}/static/bundles/commons.js`,
            `${lmsBaseUrl}/static/edx-ui-toolkit/js/utils/global-loader.js`,
            `${lmsBaseUrl}/static/edx-ui-toolkit/js/utils/string-utils.js`,
            `${lmsBaseUrl}/static/edx-ui-toolkit/js/utils/html-utils.js`,
            `${lmsBaseUrl}/static/js/src/accessibility_tools.js`,
            ...jsUrls,
        ];
    }

    const jsTags = jsUrls.map((url) => `<script type="text/javascript" src="${url}"></script>`).join('\n');

    return `

        ${jsTags}

        <script>
            $.postWithPrefix = $.post;
            $.getWithPrefix = $.get;
            $.ajaxWithPrefix = $.ajax;
            window.onTouchBasedDevice = function() { return navigator.userAgent.match(/iPhone|iPod|iPad|Android/i); };
            <!--The Video XBlock has an undeclared dependency on 'Logger' -->
            window.Logger = { log: function() { } };
        </script>
    `;
}

function getCssTags(cssUrls: string[], shouldInitializeXBlockRuntime: boolean) {

    // If we are not initializing the runtime, we don't need these css files either.
    // The css styles for all these XBlocks are defined in xblock-styles.scss which is still being included below.
    if (!shouldInitializeXBlockRuntime) {
        return '';
    }

    const lmsBaseUrl = LxConfig.LmsBaseUrl;

    // We can remove this once LMS starts returning lms-course.css package in the view response.
    if (!cssUrls.find((u) => u.includes('lms-course'))) {
        cssUrls = [
            `${lmsBaseUrl}/static/css/lms-course.css`,
            ...cssUrls,
        ];
    }
    const cssTags = cssUrls.map((url) => `<link rel="stylesheet" href="${url}">`).join('\n');
    return `
        ${cssTags}
    `;
}

/**
 * Given an XBlock's fragment data (HTML plus CSS and JS URLs), return the
 * inner HTML that should go into an IFrame in order to display that XBlock
 * and interact with the surrounding LabXchange UI and with the LMS.
 * @param html The XBlock's HTML (Fragment.content)
 * @param jsUrls A list of any JavaScript URLs the XBlock may require
 * @param cssUrls A list of any CSS URLs the XBlock may require
 * @param shouldInitializeXBlockRuntime If we know the XBlock does not have JS code, we can skip the expensive init.
 * @param extraInformation Extra variables that get passed to the inner iframe
 */
export function wrapBlockHtmlForIFrame(
    html: string,
    jsUrls: string[],
    cssUrls: string[],
    shouldInitializeXBlockRuntime: boolean,
    extraInformation?: ExtraInformation,
): string {
    const lxXBlockCssOverridesUrl = `${window.location.protocol}//${window.location.host}${xblockCssPath}`;
    const primsJS = `${window.location.protocol}//${window.location.host}/assets/tinymce/prism.js`;
    const parentListeningTarget = `${window.location.toString()}`;
    const result = `
        <!DOCTYPE html>
        <html lang="${locale}">
        <head>
            <!-- Open links in a new tab, not this iframe -->
            <base target="_blank">
            <meta charset="UTF-8">

            <!-- Show the body after the XBlock init has happened. -->
            <style type="text/css" media="screen">
                body {
                    opacity: 0;
                }
                .body-shown {
                    opacity: 1;
                    transition: opacity 0.25s ease-in-out;
                }
            </style>

            <!-- Load these first so that requests for font files can be queued. -->
            ${getCssTags(cssUrls, shouldInitializeXBlockRuntime)}
            <link rel="stylesheet" href="${lxXBlockCssOverridesUrl}">

            ${getJsTags(jsUrls, shouldInitializeXBlockRuntime)}

            <script type="text/javascript" src="https://cdn.jsdelivr.net/npm/iframe-resizer@4.2.8/js/iframeResizer.contentWindow.min.js"></script>
            <script type="text/javascript" src="${primsJS}"></script>

            <!-- Init -->
            <script>
                // We init the XBlocks as soon as DOMContentLoaded. By this event, all the static files
                // included above will have been been loaded. We do not wait for the load event
                // because the html content can contain images and we do not want XBlock init to wait for
                // large images to load. See for more details:
                // https://javascript.info/onload-ondomcontentloaded
                window.addEventListener('load', () => {
                    const innerFrame = document.getElementsByTagName('iframe')[0];
                    innerFrame.contentWindow.postMessage(${JSON.stringify(extraInformation)}, '*');
                });
                window._lxInitializeXBlockAndChildren = ${shouldInitializeXBlockRuntime}
                window.parentListeningTarget = "${parentListeningTarget}"
                window.addEventListener('DOMContentLoaded', (${blockFrameJS.toString()}));
                // Allows us to relay messages sent from iframes down below, such as in simulations.
                window.addEventListener('message', (event) => {
                  if (event.data.method === 'event') {
                    window.parent.postMessage(event.data, window.parentListeningTarget)
                  }
                });
            </script>

        </head>
        <body class="xblock-body">

            <!-- The block content html -->
            ${html}

            <!-- The following can be loaded after XBlock init. -->

            <!-- MathJax to display math. -->
            <script defer src="https://cdn.jsdelivr.net/npm/mathjax@2.7.5/MathJax.js?config=TeX-MML-AM_HTMLorMML">
                (() => {
                    const mathjax = window.MathJax;
                    mathjax.Hub.Config({
                        tex2jax: {
                            inlineMath: [['[mathjaxinline]', '[/mathjaxinline]'], ['\\\\(', '\\\\)']],
                            displayMath: [['[mathjax]', '[/mathjax]'], ['\\\\[', '\\\\]']]
                        }
                    });
                    mathjax.Hub.Configured();
                })();
            </script>

            <!-- At least one XBlock (drag and drop v2) expects Font Awesome -->
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/font-awesome@4.7.0/css/font-awesome.min.css">

        </body>
        </html>
    `;
    return result;
}

/**
 * The JavaScript code which runs inside our IFrame and is responsible
 * for communicating with the parent window.
 *
 * This cannot use any imported functions because it runs in the IFrame,
 * not in our app webpack bundle.
 */
function blockFrameJS() {
    // Polyfill for IE11:
    if (!Element.prototype.matches) {
        Element.prototype.matches = (Element.prototype as any).msMatchesSelector ||
                                    (Element.prototype as any).webkitMatchesSelector;
    }

    interface XBlockJS {
        element: XBlockElement;
    }

    const CHILDREN_KEY = '_lx_xb_children';
    const USAGE_ID_KEY = '_lx_xb_usage_id';
    const HANDLER_URL = '_lx_xb_handler_url';
    /**
     * In order to implement the XBlock JavaScript runtime API,
     * we need to store some data in the DOM on the XBlock DOM
     * element itself. This interface defines that data.
     */
    interface XBlockElement extends Element {
        [CHILDREN_KEY]: XBlockJS[];
        [USAGE_ID_KEY]: string;
        // A URL template like
        // 'http://localhost:4556/api/v1/xblocks/secure-handler/d0389377759f2b48a9d9094f1ffca699582fd4c0/handler_name/'
        // That can be used even from within an IFrame (cross-origin friendly).
        // The URL is specific to this XBlock and user.
        [HANDLER_URL]: string;
    }

    /**
     * The JavaScript runtime for any XBlock in the IFrame
     */
    const runtime = {
        /**
         * An obscure and little-used API that retrieves a particular
         * XBlock child using its 'data-name' attribute
         * @param block The root DIV element of the XBlock calling this method
         * @param childName The value of the 'data-name' attribute of the root
         *    DIV element of the XBlock child in question.
         */
        childMap: (block: XBlockElement, childName: string) => {
            return runtime.children(block).find((child) => child.element.getAttribute('data-name') === childName);
        },
        children: (block: XBlockElement) => {
            return block[CHILDREN_KEY];
        },
        /**
         * Get the URL for the specified handler. This method must be synchronous, so
         * cannot make HTTP requests.
         */
        handlerUrl: (block: XBlockElement, handlerName: string, suffix?: string, query?: string): string => {
            let url = block[HANDLER_URL].replace('handler_name', handlerName);
            if (suffix) {
                url += `${suffix}/`;
            }
            if (query) {
                url += `?${query}`;
            }
            return url;
        },
        /**
         * Pass an arbitrary message from the XBlock to the parent application.
         * This is mostly used by the studio_view to inform the user of save events.
         * Standard events are as follows:
         *
         * save: {state: 'start'|'end', message: string}
         * -> Displays a "Saving..." style message + animation to the user until called
         *    again with {state: 'end'}. Then closes the modal holding the studio_view.
         *
         * error: {title: string, message: string}
         * -> Displays an error message to the user
         *
         * cancel: {}
         * -> Close the modal holding the studio_view
         */
        notify: (eventType: string, params: any) => {
            params.method = 'xblock:' + eventType;
            postMessageToParent(params);
        },
    };

    // Recursively initialize the JavaScript code of each XBlock:
    function initializeXBlockAndChildren(element: XBlockElement, callback: () => void) {

        // If the XBlock does not have Javascript code, skip initializing the runtime.
        if (!(window as any)._lxInitializeXBlockAndChildren) {
            callback();
            return;
        }

        // The newer/pure/Blockstore runtime uses the 'data-usage' attribute, while the LMS uses 'data-usage-id'
        const usageId = element.getAttribute('data-usage') || element.getAttribute('data-usage-id');
        if (usageId !== null) {
            element[USAGE_ID_KEY] = usageId;
        } else {
            throw new Error('XBlock is missing a usage ID attribute on its root HTML node.');
        }

        // Recursively initialize any children first:
        // We need to find all div.xblock-v1 children, unless they're grandchilden
        // So we build a list of all div.xblock-v1 descendants that aren't descendants
        // of an already-found descendant:
        const childNodesFound: XBlockElement[] = [];
        [].forEach.call(element.querySelectorAll('.xblock, .xblock-v1'), (childNode: Element) => {
            if (!childNodesFound.find((el) => el.contains(childNode))) {
                childNodesFound.push(childNode as XBlockElement);
            }
        });

        // This code is awkward because we can't use promises (IE11 etc.)
        let childrenInitialized = -1;
        function initNextChild() {
            childrenInitialized++;
            if (childrenInitialized < childNodesFound.length) {
                const childNode = childNodesFound[childrenInitialized];
                initializeXBlockAndChildren(childNode, initNextChild);
            } else {
                // All children are initialized:
                initializeXBlock(element, callback);
            }
        }
        initNextChild();
    }

    /**
     * Initialize an XBlock. This function should only be called by initializeXBlockAndChildren
     * because it assumes that function has already run.
     */
    function initializeXBlock(element: XBlockElement, callback: () => void) {
        const version = element.getAttribute('data-runtime-version');
        if (version !== null && version !== '1') {
            throw new Error('Unsupported XBlock runtime version requirement.');
        }

        const usageId = element[USAGE_ID_KEY];
        // Check if the XBlock has an initialization function:
        const initFunctionName = element.getAttribute('data-init');
        if (initFunctionName !== null) {
            // Since this block has an init function, it may need to call handlers,
            // so we first have to generate a secure handler URL for it:
            postMessageToParent({method: 'get_handler_url', usageId}, (handlerData: any) => {
                element[HANDLER_URL] = handlerData.handlerUrl;
                // Now proceed with initializing the block's JavaScript:
                const initFunction = (window as any)[initFunctionName];
                // Does the XBlock HTML contain arguments to pass to the initFunction?
                let data: any = {};
                [].forEach.call(element.children, (childNode: Element) => {
                    // The newer/pure/Blockstore runtime uses 'xblock_json_init_args'
                    // while the LMS runtime uses 'xblock-json-init-args'.
                    if (
                        childNode.matches('script.xblock_json_init_args') ||
                        childNode.matches('script.xblock-json-init-args')
                    ) {
                        data = JSON.parse(childNode.textContent as string);
                    }
                });
                const blockJS: XBlockJS = new initFunction(runtime, element, data) || {};
                blockJS.element = element;
                callback();
            });
        } else {
            callback();
        }
    }

    const uniqueKeyPrefix = `k${+Date.now()}-${Math.floor(Math.random() * 1e10)}-`;
    let messageCount = 0;
    /**
     * A helper method for sending messages to the parent window of this IFrame
     * and getting a reply, even when the IFrame is securely sandboxed.
     * @param messageData The message to send. Must be an object, as we add a key/value pair to it.
     * @param callback The callback to call when the parent window replies
     */
    function postMessageToParent(messageData: any, callback?: (data: any) => void) {
        const messageReplyKey = uniqueKeyPrefix + (messageCount++);
        messageData.replyKey = messageReplyKey;
        if (callback !== undefined) {
            const handleResponse = (event: MessageEvent) => {
                if (event.source === window.parent && event.data.replyKey === messageReplyKey) {
                    callback(event.data);
                    window.removeEventListener('message', handleResponse);
                }
            };
            window.addEventListener('message', handleResponse);
        }
        window.parent.postMessage(messageData, '*');
    }

    // Find the root XBlock node.
    // The newer/pure/Blockstore runtime uses '.xblock-v1' while the LMS runtime uses '.xblock'.
    const rootNode = document.querySelector('.xblock, .xblock-v1'); // will always return the first matching element
    initializeXBlockAndChildren(rootNode as XBlockElement, () => {
        postMessageToParent({method: 'init_done'});
        showBody();
    });

    // Fade in the body after the XBlocks have been initialized.
    function showBody() {
        setTimeout(() => {
            const body = document.querySelector('body');
            if (body) {
                body.classList.add('body-shown');
            }
        }, 50);
    }
}
